import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
	RESET_SELECTED_SIM,
	SET_HOME_MODAL_VISIBILITY,
	SET_ONBOARDING_REDIRECTION_PAGE,
	SET_UPI_DATA,
	SET_UPI_STATUS_LOADING,
	useHomeModalVisibility,
	useUpiData,
	useUpiStep,
} from 'Slices';
import { IDispatcherType, IHomeModalVisibility, IModalType } from 'types';
import history from 'utils/History';
import ImportantCheckpointModal from './ImportantCheckpointModal';
import PermissionModal from './PermissionModal';
import { checkPermissions, OpenAppSettings, SavePermissions } from 'utils/nativeSupport';
import { ONBOARDING_REDIRECTION_PAGE, PERMISSIONS, UPI_ERROR_CODE, UPISteps } from 'utils/CONSTANT';
import AlertModal from './AlertModal';
import DANGER from 'assets/icons/Autopay/danger.svg';
import Toasty from 'components/atoms/Toasty';
import { isEmpty } from 'utils/Utils';
import { UPIActivateAccount } from 'utils/nativeUpiHandler';
import { useMerchantInfo } from 'Slices/MercantInfoSlice';
import CardIcon from 'assets/icons/Autopay/card.svg';
import { deviceBindingRedirectionHandle } from 'utils/deviceBindingHelper';
import { sendEvent } from 'utils/Sentry';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';
import useUpiStatus from 'Hooks/useUpiStatus';

interface IProps {
	setLoader?: (val: boolean) => void;
	upiOnboardingSource?: ONBOARDING_REDIRECTION_PAGE;
}
export interface ILimits {
	oldLimits: number;
	newLimits: number;
	difference: number;
}
export const LIMITS_INIT: ILimits = {
	oldLimits: 0,
	newLimits: 0,
	difference: 0,
};

const AllModals: React.FC<IProps> = ({ setLoader, upiOnboardingSource = ONBOARDING_REDIRECTION_PAGE.MANDATE }) => {
	const dispatch = useDispatch<IDispatcherType>();
	const modalVisibility: IHomeModalVisibility = useHomeModalVisibility();
	const upiStepDetails = useUpiStep();
	const { upiStep, upiErr } = upiStepDetails;
	const upiData = useUpiData();
	const merchantInfo = useMerchantInfo();
	const { languageData } = useLanguageConfig();
	const upiStatus = useUpiStatus(upiOnboardingSource, false);

	const closeModal = (type: IModalType) => () => {
		dispatch(SET_HOME_MODAL_VISIBILITY({ [type]: false }));
	};

	const handleImportantCheckpointContinueClick = () => {
		history.push({
			pathname: '/upi/auto-pay',
		});
	};

	const handleSaveDebitDetails = async () => {
		try {
			dispatch(SET_HOME_MODAL_VISIBILITY({ debitCardModal: false }));
			setLoader && setLoader(true);
			await UPIActivateAccount(upiData?.upiProfileDetails?.user_banks[0]);
			setLoader && setLoader(false);
			deviceBindingRedirectionHandle(false);
		} catch (err: any) {
			dispatch(SET_HOME_MODAL_VISIBILITY({ debitCardModal: false }));
			setLoader && setLoader(false);
			if (UPI_ERROR_CODE.includes(err?.errorCode)) Toasty.error(err?.error);
			else Toasty.error('Something went wrong !!!');
			sendEvent(err);
			deviceBindingRedirectionHandle(true);
		}
	};

	const setReclaimFlow = async () => {
		if (upiData?.upiProfileDetails?.user_banks[0]) {
			history.push({
				pathname: '/upi/reclaim-bank-list',
			});
		} else {
			Toasty.error('Something went wrong !!!');
		}
	};

	const routeOnTheBasisOfUpiStatus = () => {
		if (upiErr) {
			Toasty.error(upiErr);
			dispatch(SET_HOME_MODAL_VISIBILITY({ somethingWentWrong: true }));
			return;
		}
		if (!isEmpty(upiStep)) {
			dispatch(SET_ONBOARDING_REDIRECTION_PAGE({ source: upiOnboardingSource }));
		}
		switch (upiStep) {
			case UPISteps.UPGRADE:
				dispatch(RESET_SELECTED_SIM());
				history.push({ pathname: '/upi/sim-binding' });
				break;
			case UPISteps.UPI_ONBOARDING_COMPLETE:
				history.push({
					pathname: '/upi/auto-pay',
				});
				break;
			case UPISteps.RECLAIM_STATUS:
				setReclaimFlow();
				break;
			case UPISteps.BANK_SCREEN:
				history.push({
					pathname: '/upi/bank-selection',
				});
				break;
		}
	};

	const handlehHookSavePermission = () => {
		const permissions = checkPermissions();
		const userMobile = merchantInfo?.data?.mobile?.substring(2);

		permissions.then(async (data) => {
			if (!data.READ_PHONE_STATE || !data.SEND_SMS || !data.READ_SMS) {
				const permissionSaved = await SavePermissions(PERMISSIONS);
				if (permissionSaved.READ_PHONE_STATE && permissionSaved.SEND_SMS && permissionSaved.READ_SMS) {
					dispatch(SET_HOME_MODAL_VISIBILITY({ hookPermissionModal: false }));
					dispatch(SET_UPI_STATUS_LOADING(true));
					upiStatus.initTpapSdk(userMobile);
				} else {
					OpenAppSettings();
				}
			} else {
				dispatch(SET_HOME_MODAL_VISIBILITY({ hookPermissionModal: false }));
				dispatch(SET_UPI_STATUS_LOADING(true));
				upiStatus.initTpapSdk(userMobile);
			}
		});
	};

	const handleSavePermission = async () => {
		const permissions = checkPermissions();
		permissions.then(async (data) => {
			if (!data.READ_PHONE_STATE || !data.SEND_SMS || !data.READ_SMS) {
				const permissionSaved = await SavePermissions(PERMISSIONS);
				dispatch(SET_HOME_MODAL_VISIBILITY({ permissionModal: { visible: false } }));
				if (permissionSaved.READ_PHONE_STATE && permissionSaved.SEND_SMS && permissionSaved.READ_SMS) {
					logAnalyticsEvent({
						trigger_point: `Click on 'SETUP AUTOPAY'`,
						Product: 'AutoPayUPI',
						Flow: 'SetupUPI',
						Screen: 'Setup',
						Module: 'Permission_Modal',
						Element: 'CTA',
						Action: 'Tapped',
						ctaType: 'setupAutopay',
					});
					routeOnTheBasisOfUpiStatus();
				} else {
					OpenAppSettings();
				}
			} else {
				routeOnTheBasisOfUpiStatus();
			}
		});
	};

	const handlePermissionModalClose = () => {
		logAnalyticsEvent({
			trigger_point: `Click above Modal to go back`,
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'Setup',
			Module: 'AboveModal',
			Action: 'Tapped',
		});
		closeModal('hookPermissionModal');
		dispatch(SET_HOME_MODAL_VISIBILITY({ permissionModal: { visible: false } }));
	};
	const handleMaxLimitModal = () => {
		history.push({
			pathname: '/upi/sim-binding',
		});
		dispatch(SET_HOME_MODAL_VISIBILITY({ maxLimitModal: false }));
	};

	const handleSomethingWentWrong = () => {
		dispatch(SET_HOME_MODAL_VISIBILITY({ somethingWentWrong: false }));
	};

	return (
		<>
			{modalVisibility.importantCheckpointModal && (
				<ImportantCheckpointModal handleImportantCheckpointContinueClick={handleImportantCheckpointContinueClick} />
			)}

			{modalVisibility.hookPermissionModal && (
				<PermissionModal
					handleClick={handlehHookSavePermission}
					btnText={languageData.CONTINUE}
					handleModalClose={() => {
						handlePermissionModalClose;
					}}
				/>
			)}

			{modalVisibility.permissionModal?.visible && (
				<PermissionModal
					handleClick={handleSavePermission}
					btnText={modalVisibility.permissionModal?.primaryBtnText}
					handleModalClose={() => {
						handlePermissionModalClose;
					}}
				/>
			)}

			<AlertModal
				handleClick={handleMaxLimitModal}
				title={languageData.MAX_THREE_ATTEMPT_ALLOWED}
				description={languageData.YOU_ARE_ALLOWED_TO_ATTEMPT_UPI_LINKING_ONLY_3_TIMES_IN_24_HOURS}
				buttonName={languageData.DONE}
				icon={DANGER}
				type="danger"
				visible={modalVisibility.maxLimitModal}
				showCloseButton={false}
			/>

			<AlertModal
				handleClick={handleSomethingWentWrong}
				title={languageData.SOMETHING_WENT_WRONG}
				description={languageData.PLEASE_TURN_OFF_YOUR_WIFI_AND_USE_MOBILE_NETWORK_ONLY_TO_SETUP_AUTOPAY}
				buttonName={languageData.OK}
				icon={DANGER}
				type="danger"
				visible={modalVisibility.somethingWentWrong}
				showCloseButton={false}
			/>
			<AlertModal
				handleClick={handleSaveDebitDetails}
				title={languageData.SET_PIN_WITH_DEBIT_CARD}
				description={languageData.KINDLY_PROCEED_ONLY_IF_YOU_HAVE_THE_SAME_MOBILE_NUMBER_MOBILENUMBER_REGISTERED_WITH_DEBIT_CARD_KEEP_YOUR_DEBIT_CARD_WITH_YOU_TO_VARIFY_16_DIGIT_NUMBER.replace(
					'{0}',
					merchantInfo?.data?.mobile,
				)}
				buttonName={languageData.CONTINUE}
				icon={CardIcon}
				type={'info'}
				visible={modalVisibility.debitCardModal}
				showCloseButton={false}
			/>
		</>
	);
};

export default AllModals;
